import { useMutation, defaultDataIdFromObject } from '@apollo/client';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import * as immutable from 'object-path-immutable';
import { useModal } from 'components/shared/hooks';
import toast from 'helpers/toast';
import { TaskListSectionsTemplateFragment } from 'components/projects/taskList/gql/fragments';
import TASK_LIST_SECTION_TEMPLATE_DELETE from '../../gql/taskListSectionTemplateDelete.graphql.js';
import GET_TASK_LIST_TEMPLATE_HEADER_INFO from '../../gql/getTaskListTemplateHeaderInfo.graphql';
import { fireStevenEntityCrudEventForTemplate } from '../../templates/templateUpdateStevenEvent';

const useDeleteSectionTemplate = ({ templateId }) => {
  const { hideModal } = useModal();
  const { formatMessage } = useIntl();

  const [deleteTasklistTemplateSection] = useMutation(
    TASK_LIST_SECTION_TEMPLATE_DELETE,
    {
      refetchQueries: [
        {
          query: GET_TASK_LIST_TEMPLATE_HEADER_INFO,
          variables: { id: templateId },
        },
        'GetTaskListTemplateHeaderInfo',
      ],
      onCompleted: () => {
        hideModal();
        fireStevenEntityCrudEventForTemplate({
          templateNodeId: templateId,
          entityType: 'checklist_template_section',
          action: 'deleted',
        });
      },
      onError: () => {
        toast(
          formatMessage({
            id: 'Projects.taskList.taskListSection.deleteSectionError',
          }),
        );
      },
      update: (cache, { data: templateSectionData }) => {
        const deletedId = get(
          templateSectionData,
          'taskListSectionTemplateDelete.deletedId',
          null,
        );

        const cacheId = defaultDataIdFromObject({
          id: templateId,
          __typename: 'TaskListTemplate',
        });

        const cachedData = cache.readFragment({
          fragment: TaskListSectionsTemplateFragment,
          fragmentName: 'TaskListSectionsTemplateFragment',
          id: cacheId,
        });

        const sections = get(cachedData, 'taskListSectionTemplates.nodes', []);

        const sectionIndex = sections
          .map((cachedSection) => cachedSection.id)
          .indexOf(deletedId);

        if (sectionIndex >= 0) {
          const updatedData = immutable.del(
            cachedData,
            `taskListSectionTemplates.nodes.${sectionIndex}`,
          );

          cache.writeFragment({
            fragment: TaskListSectionsTemplateFragment,
            fragmentName: 'TaskListSectionsTemplateFragment',
            id: cacheId,
            data: updatedData,
          });
        }
      },
    },
  );

  return {
    deleteTasklistTemplateSection,
  };
};

export default useDeleteSectionTemplate;
